/**
 * Date ranges for filtering the tournament list.
 *
 * `TournamentDto.date` is a date-only `YYYY-MM-DD` string, so every bound here is the same
 * shape and comparison is plain lexicographic string comparison — no parsing at match time
 * and no timezone in the hot path.
 *
 * All arithmetic reads local date components and never goes through `toISOString()`, which
 * converts to UTC and would roll the date over an entire day either side of midnight for
 * anyone west of Greenwich. A room in Las Vegas would otherwise see "today" become yesterday
 * every afternoon.
 */

/**
 * 0 = Sunday, 1 = Monday. Sunday because every room on the platform today is US-based, where
 * that is the conventional week start. Change this one value to switch to ISO weeks.
 */
export const WEEK_STARTS_ON = 0;

export type DatePresetId =
  | 'yesterday'
  | 'today'
  | 'tomorrow'
  | 'lastWeek'
  | 'thisWeek'
  | 'nextWeek'
  | 'lastMonth'
  | 'thisMonth'
  | 'nextMonth';

/** Inclusive on both ends. `null` means unbounded on that side. */
export interface DateRange {
  start: string | null;
  end: string | null;
}

export const UNBOUNDED: DateRange = { start: null, end: null };

export const DATE_PRESETS: readonly { id: DatePresetId; label: string }[] = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'lastWeek', label: 'Last week' },
  { id: 'thisWeek', label: 'This week' },
  { id: 'nextWeek', label: 'Next week' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'nextMonth', label: 'Next month' },
] as const;

/** Local calendar date as `YYYY-MM-DD`. */
export function toIso(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/** Midnight local, stripped of any time component the caller's Date carried. */
function atMidnight(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const x = atMidnight(d);
  x.setDate(x.getDate() + n);
  return x;
}

function startOfWeek(d: Date): Date {
  const x = atMidnight(d);
  x.setDate(x.getDate() - ((x.getDay() - WEEK_STARTS_ON + 7) % 7));
  return x;
}

/** Day 0 of the following month is the last day of this one, leap years included. */
function monthRange(year: number, monthIndex: number): DateRange {
  return {
    start: toIso(new Date(year, monthIndex, 1)),
    end: toIso(new Date(year, monthIndex + 1, 0)),
  };
}

function dayRange(d: Date): DateRange {
  const iso = toIso(d);
  return { start: iso, end: iso };
}

function weekRange(anchor: Date): DateRange {
  const start = startOfWeek(anchor);
  return { start: toIso(start), end: toIso(addDays(start, 6)) };
}

/**
 * A preset's dates on the browser's calendar. Since performance rank 1 these never leave the browser:
 * the list sends a preset by name and the server resolves it at the room's gaming day
 * (`tournamentList.ts`). What is left reads them only to ask whether a filter is bounded and whether
 * two filters are the same — questions the browser's calendar answers correctly.
 */
export function presetRange(id: DatePresetId, now: Date = new Date()): DateRange {
  const today = atMidnight(now);

  switch (id) {
    case 'yesterday':
      return dayRange(addDays(today, -1));
    case 'today':
      return dayRange(today);
    case 'tomorrow':
      return dayRange(addDays(today, 1));
    case 'lastWeek':
      return weekRange(addDays(today, -7));
    case 'thisWeek':
      return weekRange(today);
    case 'nextWeek':
      return weekRange(addDays(today, 7));
    case 'lastMonth':
      return monthRange(today.getFullYear(), today.getMonth() - 1);
    case 'thisMonth':
      return monthRange(today.getFullYear(), today.getMonth());
    case 'nextMonth':
      return monthRange(today.getFullYear(), today.getMonth() + 1);
  }
}

/**
 * A preset and a custom range are mutually exclusive, but keeping both in state means
 * switching to a preset and back doesn't discard the dates the user typed.
 */
export interface DateFilterState {
  preset: DatePresetId | null;
  custom: DateRange;
}

export const NO_DATE_FILTER: DateFilterState = { preset: null, custom: UNBOUNDED };

/** The range actually applied to the list. Presets are resolved against today, on read. */
export function activeRange(state: DateFilterState): DateRange {
  return state.preset === null ? state.custom : presetRange(state.preset);
}

export function isUnbounded(range: DateRange): boolean {
  return range.start === null && range.end === null;
}

export function inRange(date: string, range: DateRange): boolean {
  if (range.start !== null && date < range.start) return false;
  if (range.end !== null && date > range.end) return false;
  return true;
}

const LONG = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

function human(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? iso : LONG.format(parsed);
}

/** Plain-language summary of what a range covers, for the filter's result line. */
export function describeRange(range: DateRange): string {
  const { start, end } = range;
  if (start === null && end === null) return 'any date';
  if (start !== null && end === null) return `${human(start)} onwards`;
  if (start === null && end !== null) return `up to ${human(end)}`;
  if (start === end) return human(start!);
  return `${human(start!)} – ${human(end!)}`;
}
